/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App_App } from "@@/App";
import { BrowserEvent } from "@@/Helpers";

const isProd = (() => {
  try {
    return process.env.NODE_ENV === "production";
  } catch {
    return false;
  }
})();

const elem = document.getElementById("root")!;

const layout = (
  <StrictMode>
    <App_App />
  </StrictMode>
);

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(layout);
} else {
  // The hot module reloading API is not available in production.
  createRoot(elem).render(layout);
}

function forEvent(eventTarget: EventTarget, eventName: string): Promise<void> {
  return new Promise<void>((resolve) => {
    eventTarget.addEventListener(eventName, () => resolve(), { once: true });
  });
}

let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

function onForeground() {
  console.log("App ist im Vordergrund");
  serviceWorkerRegistration?.update();
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    onForeground();
  }
});

if ("serviceWorker" in navigator && isProd) {
  window.addEventListener("load", async () => {
    let registration = await navigator.serviceWorker.register("/worker.js");
    serviceWorkerRegistration = registration;

    const doUpdate = async () => {
      if (registration.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });

        // 3. Wenn der aktive Controller wechselt -> Seite neu laden
        await forEvent(navigator.serviceWorker, "controllerchange");
        console.log("Neuer Service Worker ist aktiv – Seite wird neu geladen.");
        window.location.reload();
      }
    };

    let notify = () => {
      BrowserEvent.notifyUpdateAvailable(doUpdate);
    };

    if (registration.waiting) {
      console.log("Service Worker wartet (Notify).");
      notify();
    }

    await forEvent(registration, "updatefound");

    // 1. Neue Version gefunden
    const newWorker = registration.installing;
    if (!newWorker) return;

    await forEvent(newWorker, "statechange");

    // 2. Warten bis die neue Version installiert ist
    if (newWorker.state === "installed") {
      // navigator.serviceWorker.controller existiert nur,
      // wenn bereits ein SW aktiv ist -> also Update-Fall
      if (navigator.serviceWorker.controller) {
        console.log("Service Worker installiert (Notify).");
        notify();
      } else {
        // Erstinstallation – meistens kein Dialog nötig
        console.log("Service Worker installiert (Erstinstallation).");
      }
    }
  });
}
